import React, { useMemo, useRef, useState } from "react";
import { buildClaimSummary, downloadTextFile, validateClaimPacket, saveBytesAsPdf } from "@/lib/claimUtils";
import { createMasterClaimPdf } from "@/lib/masterPdf";
import { createExamWorksheetPdf } from "@/lib/examWorksheetPdf";
import { listPrescriptions } from "@/lib/api";
import { showToast } from "@/lib/toast";
import { VIEWS } from "@/lib/navConfig";
import { Export, Printer, DownloadSimple, ClipboardText, CheckCircle, WarningCircle, XCircle, FilePdf, Sparkle, Notepad } from "@phosphor-icons/react";

function getScoreClass(score) {
  if (score >= 80) return "score--good";
  if (score >= 50) return "score--warn";
  return "score--bad";
}

export default function ExportView({ workspace, profile, financials, issues, evidence, combined, master526ez, form0781, statements, currentSnapshot, isPro = true, setActiveView }) {
  const claimSummary = useMemo(
    () => buildClaimSummary({ workspace, profile, financials, issues, evidence, combined }),
    [workspace, profile, financials, issues, evidence, combined]
  );
  const validation = useMemo(
    () => validateClaimPacket({ issues, evidence, master526ez, form0781 }),
    [issues, evidence, master526ez, form0781]
  );
  const printFrameRef = useRef(null);
  const [generating, setGenerating] = useState(false);
  const [pdfStatus, setPdfStatus] = useState("");
  const [worksheetExamType, setWorksheetExamType] = useState("cp");
  const [worksheetBusy, setWorksheetBusy] = useState(false);
  const [worksheetConditionId, setWorksheetConditionId] = useState("all"); // "all" | issue.id

  async function generateExamWorksheets() {
    setWorksheetBusy(true);
    try {
      const prescriptions = await listPrescriptions();
      // Honor the per-condition selector — if the user picked one specific
      // condition, only render that worksheet; otherwise generate the full set.
      const selectedConditions = worksheetConditionId === "all"
        ? issues
        : issues.filter((i) => i.id === worksheetConditionId);
      const bytes = await createExamWorksheetPdf({
        veteranName: profile.name || "Veteran",
        master526ez,
        appointment: null,
        conditions: selectedConditions,
        prescriptions,
        examType: worksheetExamType,
      });
      const safeName = (profile.name || "Veteran").replace(/[^a-zA-Z0-9-_]+/g, "-");
      const stamp = new Date().toISOString().slice(0, 10);
      const conditionSlug = worksheetConditionId === "all"
        ? "All-Conditions"
        : (issues.find((i) => i.id === worksheetConditionId)?.claimedAs || "Condition")
            .replace(/[^a-zA-Z0-9-_]+/g, "-").slice(0, 40);
      saveBytesAsPdf(bytes, `ClaimOps-Exam-Worksheet-${conditionSlug}-${safeName}-${stamp}.pdf`);
      const n = selectedConditions.length;
      showToast({ variant: "success", duration: 3000, message: `Generated ${n} worksheet${n === 1 ? "" : "s"}.` });
    } catch (e) {
      showToast({ variant: "error", message: `Could not generate worksheets: ${e.message}` });
    } finally {
      setWorksheetBusy(false);
    }
  }

  async function generateMasterPdf() {
    setGenerating(true);
    setPdfStatus("");
    try {
      const bytes = await createMasterClaimPdf({
        workspace, profile, financials, issues, evidence, master526ez, statements: statements || [], combined,
        isPro,
      });
      const safeTitle = (workspace?.title || "VA-Claim").replace(/[^a-zA-Z0-9-_]+/g, "-");
      saveBytesAsPdf(bytes, `BeTheNexus-${safeTitle}-Claim-Package${isPro ? "" : "-FREE"}.pdf`);
      setPdfStatus(isPro ? "Generated branded claim package PDF." : "Generated FREE PDF (with watermark). Upgrade to Pro to remove.");
    } catch (err) {
      console.error("Master PDF gen failed:", err);
      setPdfStatus(`Error generating PDF: ${err.message}`);
    } finally {
      setGenerating(false);
    }
  }

  function printPackage() {
    const safe = claimSummary.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;");
    const html = `<!doctype html><html><head><title>ClaimOps VA Claim Package</title><style>body{font-family:'IBM Plex Sans',Arial,sans-serif;color:#0f172a;margin:32px;line-height:1.5}pre{white-space:pre-wrap;font-size:12px;font-family:'JetBrains Mono',monospace}@media print{button{display:none}}</style></head><body><button onclick="window.print()" style="padding:10px 16px;font-weight:bold;margin-bottom:16px;cursor:pointer">Print</button><h1>ClaimOps VA Claim Package</h1><pre>${safe}</pre></body></html>`;
    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const win = window.open(url, "_blank");
    if (!win) {
      window.print();
    }
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  }

  function copyToClipboard() {
    navigator.clipboard.writeText(claimSummary).catch((err) => console.error("Clipboard copy failed:", err));
  }

  return (
    <div className="view-grid" data-testid="export-view">
      <div className="panel master-pdf-panel" data-testid="master-pdf-panel">
        <div className="master-pdf-inner">
          <div className="master-pdf-left">
            <div className="master-pdf-tag">
              <Sparkle size={12} weight="fill" /> BRANDED PACKAGE
            </div>
            <h2 className="master-pdf-title">BeTheNexus Claim Package PDF</h2>
            <p className="master-pdf-desc">
              One downloadable PDF containing the cover page, readiness brief, condition matrix,
              evidence index, missing-evidence action plan, statement packet, C&amp;P prep sheets,
              VA math summary, page numbers, branded watermark, and full disclaimer.
            </p>
            <ul className="master-pdf-list">
              <li>Cover with veteran name, claim type, key metrics</li>
              <li>"Planning document only" watermark on every working page</li>
              <li>Per-page footer with page numbers + BeTheNexus.com branding</li>
            </ul>
          </div>
          <div className="master-pdf-right">
            <button className="btn btn--accent master-pdf-btn" onClick={generateMasterPdf} disabled={generating} data-testid="generate-master-pdf-btn">
              <FilePdf size={18} weight="bold" />
              {generating ? "GENERATING..." : isPro ? "GENERATE BRANDED PDF" : "GENERATE FREE PDF (watermarked)"}
            </button>
            {!isPro && setActiveView && (
              <button className="btn btn--outline master-pdf-upgrade" onClick={() => setActiveView(VIEWS.billing)} data-testid="export-upgrade-btn">
                Remove watermark - Upgrade to Pro ($9.99/mo)
              </button>
            )}
            {pdfStatus && <div className="master-pdf-status" data-testid="master-pdf-status">{pdfStatus}</div>}
          </div>
        </div>
      </div>

      <div className="panel" data-testid="worksheet-panel">
        <div className="panel-header">
          <Notepad size={18} weight="bold" />
          <span>EXAM DAY WORKSHEETS</span>
          <span className="header-count mono">{issues.length} condition{issues.length === 1 ? "" : "s"}</span>
        </div>
        <div className="panel-body">
          <p className="panel-desc" style={{ marginTop: 0 }}>
            Printable one-page worksheets — one per condition. Top half is patient-facing (today&apos;s symptoms checklist, severity scale, meds taken, questions to ask, what&apos;s changed). Bottom half is examiner-facing (vitals box, condition-specific objective checkpoints, signature block). Bring them to the exam; both you and the doctor will know exactly what to discuss.
          </p>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginTop: 8 }}>
            <div style={{ display: "flex", gap: 6 }} role="radiogroup" aria-label="Exam type">
              {[
                { id: "cp",      label: "VA C&P EXAM" },
                { id: "ime",     label: "IME EXAM" },
                { id: "routine", label: "ROUTINE VISIT" },
              ].map((opt) => {
                const active = worksheetExamType === opt.id;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    onClick={() => setWorksheetExamType(opt.id)}
                    data-testid={`worksheet-exam-${opt.id}`}
                    style={{
                      background: active ? "var(--branch-accent, #1D4ED8)" : "var(--surface-2)",
                      color: active ? "#fff" : "var(--text-primary)",
                      border: "1px solid",
                      borderColor: active ? "var(--branch-accent, #1D4ED8)" : "var(--border)",
                      borderRadius: 4, padding: "6px 12px", fontSize: 11, fontWeight: 800,
                      letterSpacing: "0.04em", cursor: "pointer",
                      transition: "background-color 180ms ease, color 180ms ease, border-color 180ms ease",
                    }}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
            <label className="field" style={{ minWidth: 240, marginLeft: 4 }}>
              <span className="field-label">CONDITION</span>
              <select
                className="field-input"
                value={worksheetConditionId}
                onChange={(e) => setWorksheetConditionId(e.target.value)}
                disabled={issues.length === 0}
                data-testid="worksheet-condition-select"
                style={{ padding: "6px 10px" }}
              >
                <option value="all">All conditions ({issues.length})</option>
                {issues.map((iss) => (
                  <option key={iss.id} value={iss.id}>
                    {iss.claimedAs}{iss.code ? ` — DC ${iss.code}` : ""}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className="btn btn--accent"
              onClick={generateExamWorksheets}
              disabled={worksheetBusy || issues.length === 0}
              data-testid="generate-worksheets-btn"
              style={{ marginLeft: "auto" }}
            >
              <Notepad size={14} weight="bold" />
              {worksheetBusy
                ? "GENERATING..."
                : `GENERATE WORKSHEET${worksheetConditionId === "all" && issues.length > 1 ? `S (${issues.length})` : ""}`}
            </button>
          </div>
          {issues.length === 0 && (
            <div className="form-report" style={{ marginTop: 12, fontSize: 12 }}>
              Add at least one condition to the workspace to generate worksheets.
            </div>
          )}
        </div>
      </div>

      <div className="panel" data-testid="validation-panel">
        <div className="panel-header">
          <CheckCircle size={18} weight="bold" />
          <span>CLAIM VALIDATION</span>
          <span className={`header-score mono ${getScoreClass(validation.score)}`} data-testid="validation-score">{validation.score}/100</span>
        </div>
        <div className="panel-body">
          {validation.failures.length > 0 && (
            <div className="validation-section" data-testid="validation-failures">
              <div className="validation-title"><XCircle size={16} weight="fill" className="text-red" /> FAILURES</div>
              {validation.failures.map((f) => <div key={`fail-${f}`} className="validation-item validation-item--fail">{f}</div>)}
            </div>
          )}
          {validation.warnings.length > 0 && (
            <div className="validation-section" data-testid="validation-warnings">
              <div className="validation-title"><WarningCircle size={16} weight="fill" className="text-warn" /> WARNINGS</div>
              {validation.warnings.map((w) => <div key={`warn-${w}`} className="validation-item validation-item--warn">{w}</div>)}
            </div>
          )}
          {validation.failures.length === 0 && validation.warnings.length === 0 && (
            <div className="validation-pass" data-testid="validation-pass">
              <CheckCircle size={20} weight="fill" /> All checks passed.
            </div>
          )}
        </div>
      </div>

      <div className="export-cols" data-testid="export-cols">
        <div className="panel" data-testid="export-actions-panel">
          <div className="panel-header">
            <Export size={18} weight="bold" />
            <span>EXPORT / PRINT</span>
          </div>
          <div className="panel-body">
            <div className="export-btns">
              <button className="btn btn--accent" onClick={() => downloadTextFile("claimops-va-claim-package.txt", claimSummary)} data-testid="download-txt-btn">
                <DownloadSimple size={16} weight="bold" /> DOWNLOAD TXT
              </button>
              <button className="btn btn--outline" onClick={printPackage} data-testid="print-btn">
                <Printer size={16} weight="bold" /> PRINT
              </button>
              <button className="btn btn--outline" onClick={() => downloadTextFile("claimops-va-backup.json", JSON.stringify(currentSnapshot, null, 2), "application/json;charset=utf-8")} data-testid="download-json-btn">
                <DownloadSimple size={16} weight="bold" /> BACKUP JSON
              </button>
              <button className="btn btn--ghost" onClick={copyToClipboard} data-testid="copy-btn">
                <ClipboardText size={16} weight="bold" /> COPY TO CLIPBOARD
              </button>
            </div>
          </div>
        </div>

        <div className="panel" data-testid="preview-panel">
          <div className="panel-header">
            <ClipboardText size={18} weight="bold" />
            <span>PREVIEW</span>
          </div>
          <div className="panel-body">
            <pre className="export-preview mono" data-testid="claim-preview">{claimSummary}</pre>
          </div>
        </div>
      </div>
    </div>
  );
}
